import { Button, Form } from 'react-bootstrap';
import { Field, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import spinnerTitle from 'components/others/spinnerTitle';
import SolarIcon from 'components/icons/SolarIcon';
import { useToast } from 'context/ToastContext';
import { createProcessCategory } from 'api/apiTask';

interface AddCategoryFormProps {
    onCreated: () => Promise<void>;
}

const AddCategoryForm = ({ onCreated }: AddCategoryFormProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const refInput = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const timer = window.setTimeout(() => refInput.current?.focus(), 100);
        return () => window.clearTimeout(timer);
    }, []);

    return (
        <Formik
            initialValues={{ name: '' }}
            onSubmit={async (values, { resetForm, setFieldError }) => {
                const resCreate = await createProcessCategory(values.name);
                if (resCreate?.stat) {
                    await onCreated();
                    resetForm();
                    showToast(t('dirs.messCreated'), 'success');
                    return;
                }

                if (resCreate?.errCode === 'DUPLICATE_NAME') {
                    setFieldError('name', t('dirs.messDuplicate'));
                    return;
                }
                if (resCreate?.errCode === 'EMPTY_NAME') {
                    setFieldError('name', t('dirs.messEmpty'));
                    return;
                }
                if (resCreate?.errCode === 'NAME_TOO_LONG') {
                    setFieldError('name', t('dirs.messTooLong'));
                    return;
                }

                showToast(t('common:messError'), 'danger');
            }}
        >
            {({ handleSubmit, isSubmitting, errors, touched }) => {
                const nameError = touched.name && errors.name ? errors.name : '';
                return (
                    <Form onSubmit={handleSubmit} className="mx-3 mb-3 p-3 border rounded">
                        <Form.Group className="mb-0">
                            <label className="form-label text-muted" htmlFor="addProcessCategoryName">
                                {t('dirs.labelName')}
                            </label>
                            <div className="d-flex gap-2">
                                <Field
                                    innerRef={refInput}
                                    required
                                    id="addProcessCategoryName"
                                    name="name"
                                    className={`form-control ${nameError ? 'is-invalid' : ''}`}
                                    placeholder={t('dirs.placeholderName')}
                                />
                                <Button type="submit" variant="success" disabled={isSubmitting} className="flex-shrink-0">
                                    {spinnerTitle(
                                        <>
                                            <SolarIcon name="plus" className="fs-14" />
                                            <span className="ms-1">{t('common:buttons.create')}</span>
                                        </>,
                                        isSubmitting,
                                        t('common:buttons.creating'),
                                    )}
                                </Button>
                            </div>
                            {nameError ? <div className="invalid-feedback d-block">{nameError}</div> : null}
                        </Form.Group>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default AddCategoryForm;
